// src/components/MonthlyEarnings.jsx
import React, { useEffect, useMemo, useState } from "react"
import { Row, Col, Card, CardBody } from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { useSelector } from "react-redux"
import config from "config/config"
import axios from "axios"
import "../../newcomponents/index.css"

const MONTH_WINDOW = 6 // why: keeps UI readable and consistent

function formatMonthYear(date) {
  return date.toLocaleString("default", { month: "short", year: "numeric" })
}

function getLastNMonths(n) {
  const out = []
  const d = new Date()
  d.setDate(1) // normalize to first of month
  for (let i = n - 1; i >= 0; i--) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1)
    out.push(formatMonthYear(dt))
  }
  return out
}

const MonthlyEarnings = props => {
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [doctors, setDoctors] = useState([])
  const [chartOptions, setChartOptions] = useState({
    options: {
      colors: ["#2990DF", "#F0F1F4"],
      chart: { toolbar: { show: false } },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 1.5 },
      grid: {
        borderColor: "#f8f8fa",
        row: { colors: ["transparent", "transparent"], opacity: 0.5 },
      },
      xaxis: {
        categories: [],
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          formatter(val) {
            return Number(val).toFixed(1)
          },
        },
        min: 0,
        max: 5, // why: typical rating scale; adjust if backend differs
      },
      legend: { show: true },
    },
    series: [],
  })

  const integrationId = activeOrganization?.HealthgradeIntegrations[0]?.id
  const token = userData?.token

  const authHeaders = useMemo(
    () =>
      token
        ? {
            headers: { Authorization: `Bearer ${token}` },
          }
        : null,
    [token],
  )

  const fetchDoctors = async () => {
    if (!integrationId || !authHeaders) return
    try {
      const { data } = await axios.get(
        `${config.backendUrl}/healthgrade/getDoctors?integrationId=${integrationId}`,
        authHeaders,
      )
      setDoctors(Array.isArray(data?.doctors) ? data.doctors : [])
    } catch (err) {
      props?.setErr?.("Error fetching doctors.")
    }
  }

  useEffect(() => {
    if (!integrationId || !authHeaders) return
    fetchDoctors()
  }, [integrationId, authHeaders])

  useEffect(() => {
    const months = getLastNMonths(MONTH_WINDOW)

    const series = (doctors || []).map(doc => {
      const name = doc?.doctorName ?? "Unknown"
      const ratingRaw = doc?.rating
      const rating =
        typeof ratingRaw === "number"
          ? ratingRaw
          : Number.parseFloat(ratingRaw) || 0
      return {
        name,
        data: months.map(() => rating),
      }
    })

    setChartOptions(prev => ({
      ...prev,
      options: {
        ...prev.options,
        xaxis: {
          ...prev.options.xaxis,
          categories: months,
        },
      },
      series,
    }))
  }, [doctors])

  return (
    <React.Fragment>
      <Card
        style={{
          padding: "0px 0px 35px 0px",
        }}
        className="br"
      >
        <CardBody>
          <h4 className="card-title mb-4">Doctor Ratings Trends</h4>

          <Row className="text-center mt-4">
            <Col xs="12">
              <p className="text-muted">
                Each line reflects a doctor’s current rating (from the doctor
                record) across the last {MONTH_WINDOW} months.
              </p>
            </Col>
          </Row>

          <div
            id="doctor-ratings-chart"
            className="morris-charts morris-charts-height"
            dir="ltr"
            style={{
              paddingTop: "35px",
            }}
          >
            <ReactApexChart
              options={chartOptions.options}
              series={chartOptions.series}
              type="line"
              height="300"
            />
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  )
}

export default MonthlyEarnings
